import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/shared/reducers/user-management';
import { IAchievements } from 'app/shared/model/achievements.model';
import { getEntities as getAchievements } from 'app/entities/achievements/achievements.reducer';
import { IUserAchievements } from 'app/shared/model/user-achievements.model';
import { getEntity, updateEntity, createEntity, reset } from './user-achievements.reducer';

export const UserAchievementsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const achievements = useAppSelector(state => state.achievements.entities);
  const userAchievementsEntity = useAppSelector(state => state.userAchievements.entity);
  const loading = useAppSelector(state => state.userAchievements.loading);
  const updating = useAppSelector(state => state.userAchievements.updating);
  const updateSuccess = useAppSelector(state => state.userAchievements.updateSuccess);

  const handleClose = () => {
    navigate('/user-achievements' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getAchievements({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.submittedDate = convertDateTimeToServer(values.submittedDate);
    values.createdDate = convertDateTimeToServer(values.createdDate);

    const entity = {
      ...userAchievementsEntity,
      ...values,
      user: users.find(it => it.id.toString() === values.user.toString()),
      achievements: achievements.find(it => it.id.toString() === values.achievements.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          submittedDate: displayDefaultDateTime(),
          createdDate: displayDefaultDateTime(),
        }
      : {
          ...userAchievementsEntity,
          submittedDate: convertDateTimeFromServer(userAchievementsEntity.submittedDate),
          createdDate: convertDateTimeFromServer(userAchievementsEntity.createdDate),
          user: userAchievementsEntity?.user?.id,
          achievements: userAchievementsEntity?.achievements?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="fivePlusApp.userAchievements.home.createOrEditLabel" data-cy="UserAchievementsCreateUpdateHeading">
            <Translate contentKey="fivePlusApp.userAchievements.home.createOrEditLabel">Create or edit a UserAchievements</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="user-achievements-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.fileName')}
                id="user-achievements-fileName"
                name="fileName"
                data-cy="fileName"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.status')}
                id="user-achievements-status"
                name="status"
                data-cy="status"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.submittedDate')}
                id="user-achievements-submittedDate"
                name="submittedDate"
                data-cy="submittedDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.originalFileName')}
                id="user-achievements-originalFileName"
                name="originalFileName"
                data-cy="originalFileName"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.rejectionReason')}
                id="user-achievements-rejectionReason"
                name="rejectionReason"
                data-cy="rejectionReason"
                type="text"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.createdDate')}
                id="user-achievements-createdDate"
                name="createdDate"
                data-cy="createdDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('fivePlusApp.userAchievements.externalLink')}
                id="user-achievements-externalLink"
                name="externalLink"
                data-cy="externalLink"
                type="text"
              />
              <ValidatedField
                id="user-achievements-user"
                name="user"
                data-cy="user"
                label={translate('fivePlusApp.userAchievements.user')}
                type="select"
              >
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="user-achievements-achievements"
                name="achievements"
                data-cy="achievements"
                label={translate('fivePlusApp.userAchievements.achievements')}
                type="select"
              >
                <option value="" key="0" />
                {achievements
                  ? achievements.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/user-achievements" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserAchievementsUpdate;
